class StepNavigationController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  // Texto + Estampa ganha uma etapa extra: Estilo → Estampa → Texto → Revisão
  hasSplitContent() {
    return this.orchestrator.selectionState.style === 'textprint';
  }

  getStepOrder() {
    return this.hasSplitContent()
      ? ['style', 'art', 'content', 'review']
      : ['style', 'content', 'review'];
  }

  getStepNumber(key) {
    const index = this.getStepOrder().indexOf(key);
    return index < 0 ? 0 : index + 1;
  }

  isMobileLayout() {
    return this.orchestrator.isCompactLayout('(max-width: 989px)');
  }

  // Mobile: na etapa Estilo, a escolha Tinta/Laser substitui a grade de estilos (fade), sem nova etapa
  setStyleView(view) {
    const el = this.orchestrator.dom.modal.querySelector('.style-step-content');
    if (!el) return;
    const target = view;
    if ((el.dataset.view || 'styles') === target) return;
    const header = el.querySelector('.style-header');
    const h3 = header?.querySelector('h3');
    const p = header?.querySelector('p');
    if (h3 && !h3.dataset.original) h3.dataset.original = h3.textContent;
    if (p && !p.dataset.original) p.dataset.original = p.textContent;
    el.classList.add('is-fading');
    clearTimeout(this.styleViewTimer);
    this.styleViewTimer = setTimeout(() => {
      el.dataset.view = target;
      const isMethod = target === 'method';
      if (h3) h3.textContent = isMethod ? 'Tipo de personalização' : h3.dataset.original;
      if (p) p.textContent = isMethod ? 'Como vamos aplicar o seu texto?' : p.dataset.original;
      const scroller = this.orchestrator.dom.customizerSidebar;
      if (scroller) scroller.scrollTop = 0;
      requestAnimationFrame(() => el.classList.remove('is-fading'));
    }, 180);
  }

  getActiveStepKey() {
    if (this.activeStepKey && this.getStepOrder().includes(this.activeStepKey)) {
      return this.activeStepKey;
    }
    const active = this.orchestrator.dom.modal.querySelector(
      '.customizer-sidebar .steps [data-content-step].active',
    );
    return active?.dataset.contentStep || 'style';
  }

  canLeaveStep(key) {
    const { selectionState } = this.orchestrator;
    if (key === 'style') return selectionState.isStyleComplete();
    if (key === 'art') return Boolean(selectionState.getSelectedArt());
    if (key === 'content') return this.canNavigateToFinish();
    return false;
  }

  isStepReachable(key) {
    const order = this.getStepOrder();
    const number = this.getStepNumber(key);
    if (!number) return false;
    if (number === 1) return true;
    if (number > this.orchestrator.selectionState.reached) return false;
    return order.slice(0, number - 1).every(prev => this.canLeaveStep(prev));
  }

  canNavigateToTexts() {
    return this.orchestrator.artAllowsText(
      this.orchestrator.selectionState.getSelectedArt(),
    );
  }

  canNavigateToFinish() {
    const { selectionState } = this.orchestrator;
    const hasText = Boolean(selectionState.customText.trim());
    const hasArt = Boolean(selectionState.getSelectedArt());
    return (
      (!selectionState.requiresText() || hasText) &&
      (!selectionState.requiresArt() || hasArt)
    );
  }

  getNextStepKey(currentStep = this.getActiveStepKey()) {
    const order = this.getStepOrder();
    return order[order.indexOf(currentStep) + 1] || null;
  }

  getPreviousStepKey(currentStep = this.getActiveStepKey()) {
    const order = this.getStepOrder();
    return order[order.indexOf(currentStep) - 1] || null;
  }

  syncStepNavAvailability() {
    const { navSteps } = this.orchestrator.dom;
    const split = this.hasSplitContent();
    navSteps.forEach(button => {
      const key = button.dataset.navStep;
      const number = this.getStepNumber(key);
      button.hidden = !number;
      const numberEl = button.querySelector('.step__number');
      if (numberEl && number) numberEl.textContent = String(number);
      const titleEl = button.querySelector('.step__title');
      if (titleEl && key === 'content') titleEl.textContent = split ? 'Texto' : 'Conteúdo';
      const enabled = this.isStepReachable(key);
      button.disabled = !enabled;
      button.classList.toggle('disabled', !enabled);
      button.classList.toggle('completed', number < this.orchestrator.selectionState.step);
      button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    });
  }

  syncNextStepButton() {
    const { btnNextStep, btnBackStep, finishAddCartBtn, finishCheckoutQrBtn } =
      this.orchestrator.dom;
    if (!btnNextStep) return;

    const currentStep = this.getActiveStepKey();
    const nextStep = this.getNextStepKey(currentStep);
    const isFinish = currentStep === 'review';
    btnNextStep.textContent = 'Continuar';
    if (btnBackStep) btnBackStep.hidden = currentStep === 'style';

    if (finishAddCartBtn) {
      finishAddCartBtn.hidden = !isFinish;
    }
    if (finishCheckoutQrBtn) {
      finishCheckoutQrBtn.hidden = !isFinish;
    }

    if (isFinish || !nextStep) {
      btnNextStep.hidden = true;
      btnNextStep.disabled = true;
      btnNextStep.classList.add('disabled');
      return;
    }

    btnNextStep.hidden = false;

    const shouldDisable = !this.canLeaveStep(currentStep);
    btnNextStep.disabled = shouldDisable;
    btnNextStep.classList.toggle('disabled', shouldDisable);
    btnNextStep.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
  }

  syncFooterNotes() {
    const { footerInkNote } = this.orchestrator.dom;
    if (!footerInkNote) return;
    const { style } = this.orchestrator.selectionState;
    footerInkNote.hidden = !(
      this.getActiveStepKey() === 'style' &&
      (style === 'textprint' || style === 'prints')
    );
  }

  syncContentStep(stepKey = this.getActiveStepKey()) {
    const {
      contentStepSubtitle,
      contentBlocks,
      contentTextLabel,
      textsColorSectionDefault,
      textsColorSectionDual,
      laserColorSection,
    } = this.orchestrator.dom;
    const { selectionState, textCustomizationController, artsPanelController } =
      this.orchestrator;
    const subtitles = {
      text: 'Texto, fonte e cor',
      mono: 'Iniciais, fonte e cor',
      textprint: 'Texto, fonte e cor',
      prints: 'Escolha a estampa',
    };
    const split = this.hasSplitContent();
    const isArtPhase = split && stepKey === 'art';
    const contentTitle = this.orchestrator.dom.modal.querySelector('.content-header h3');
    if (contentTitle) contentTitle.textContent = isArtPhase ? 'Estampa' : split ? 'Texto' : 'Conteúdo';
    if (contentStepSubtitle) {
      contentStepSubtitle.textContent = isArtPhase
        ? 'Escolha a estampa'
        : subtitles[selectionState.style] || '';
    }
    contentBlocks.forEach(block => {
      const type = block.dataset.contentBlock;
      let visible = type === 'art'
        ? selectionState.requiresArt()
        : selectionState.requiresText();
      if (split) visible = type === 'art' ? isArtPhase : !isArtPhase;
      block.hidden = !visible;
    });
    if (contentTextLabel) {
      contentTextLabel.textContent = selectionState.style === 'mono' ? 'Iniciais' : 'Texto';
    }
    if (textsColorSectionDefault) {
      textsColorSectionDefault.hidden = selectionState.method === 'laser';
    }
    if (laserColorSection) {
      laserColorSection.hidden = selectionState.method !== 'laser';
    }
    if (textsColorSectionDual) textsColorSectionDual.hidden = true;

    textCustomizationController.setTextLayout(
      selectionState.style === 'mono' ? 'initials' : 'fullname',
    );
    artsPanelController.renderArtsGrid();
    textCustomizationController.syncTextLayoutOptions();
  }

  setActiveStep(stepKey) {
    const { navSteps, contentSteps } = this.orchestrator.dom;
    if (!stepKey) return;
    // Sem estampa escolhida, "voltar ao conteúdo" no Texto + Estampa volta para a etapa Estampa
    if (stepKey === 'content' && this.hasSplitContent() && !this.canLeaveStep('art')) stepKey = 'art';
    const targetStep = this.getStepNumber(stepKey);
    if (!targetStep || targetStep > this.orchestrator.selectionState.reached) return;

    this.activeStepKey = stepKey;
    const panelKey = stepKey === 'art' ? 'content' : stepKey;

    navSteps.forEach(button => {
      button.classList.toggle('active', button.dataset.navStep === stepKey);
    });

    contentSteps.forEach(panel => {
      panel.classList.toggle('active', panel.dataset.contentStep === panelKey);
    });

    this.orchestrator.selectionState.step = targetStep;

    if (panelKey === 'content') this.syncContentStep(stepKey);
    const scroller = this.orchestrator.dom.customizerSidebar;
    if (scroller) scroller.scrollTop = 0;

    if (stepKey === 'review') {
      this.orchestrator.finishCheckoutController.syncFinishStep();
    }

    this.syncStepNavAvailability();
    this.syncNextStepButton();
    this.syncFooterNotes();
  }

  // Barra de rolagem própria da galeria de estampas (a nativa some sozinha no iOS/macOS)
  setupArtsScrollbar() {
    const { modal } = this.orchestrator.dom;
    const host = modal.querySelector('.step-content-legacy .step-arts');
    if (!host) return;
    const attach = () => {
      const grid = host.querySelector('.arts-grid');
      if (!grid || grid.dataset.pmbBar === 'true') return;
      grid.dataset.pmbBar = 'true';
      const bar = document.createElement('div');
      bar.className = 'arts-scrollbar';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="arts-scrollbar__thumb"></span>';
      grid.insertAdjacentElement('afterend', bar);
      const thumb = bar.firstElementChild;
      const update = () => {
        const max = grid.scrollWidth - grid.clientWidth;
        bar.hidden = max <= 1;
        if (max <= 1) return;
        const ratio = grid.clientWidth / grid.scrollWidth;
        const w = Math.max(ratio * 100, 12);
        thumb.style.width = w + '%';
        thumb.style.left = (grid.scrollLeft / max) * (100 - w) + '%';
      };
      grid.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      new MutationObserver(update).observe(grid, { childList: true });
      if (window.ResizeObserver) new ResizeObserver(update).observe(grid);
      update();
    };
    attach();
    new MutationObserver(attach).observe(host, { childList: true, subtree: true });
  }

  initialize() {
    const { modal, navSteps, btnNextStep, btnBackStep, styleButtons, methodButtons, methodSection, footerInkNote } = this.orchestrator.dom;
    if (modal.dataset.stepsReady === 'true') return;
    modal.dataset.stepsReady = 'true';

    navSteps.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled || button.classList.contains('disabled')) return;
        this.setActiveStep(button.dataset.navStep);
      });
    });

    styleButtons.forEach(button => {
      button.addEventListener('click', () => {
        const { selectionState } = this.orchestrator;
        const styleChanged = selectionState.style !== button.dataset.style;
        if (styleChanged) this.activeStepKey = 'style';
        if (styleChanged && selectionState.selectedArtKey) {
          this.orchestrator.artsPanelController.clearSelectedArt();
        }
        selectionState.setStyle(button.dataset.style);
        this.orchestrator.artsPanelController.renderArtsGrid();
        if (this.orchestrator.bottleSceneController.bodyMaterial) {
          this.orchestrator.bottleSceneController.applySelection();
        }
        styleButtons.forEach(item => {
          const selected = item === button;
          item.classList.toggle('active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        methodButtons.forEach(item => {
          item.classList.remove('active');
          item.setAttribute('aria-pressed', 'false');
        });
        const needsMethod =
          (selectionState.style === 'text' || selectionState.style === 'mono') && selectionState.laserEnabled;
        methodSection.hidden = !needsMethod;
        footerInkNote.hidden = needsMethod;
        this.setStyleView(needsMethod ? 'method' : 'styles');
        this.syncNextStepButton();
        this.syncFooterNotes();
        this.syncStepNavAvailability();
      });
    });

    modal.querySelector('[data-method-back]')?.addEventListener('click', () => {
      this.setStyleView('styles');
    });

    methodButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.orchestrator.selectionState.setMethod(button.dataset.method);
        methodButtons.forEach(item => {
          const selected = item === button;
          item.classList.toggle('active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        this.syncContentStep();
        if (this.orchestrator.bottleSceneController.bodyMaterial) {
          this.orchestrator.bottleSceneController.applySelection();
        }
        this.syncNextStepButton();
        this.syncStepNavAvailability();
      });
    });

    if (btnNextStep) {
      btnNextStep.addEventListener('click', () => {
        if (
          btnNextStep.disabled ||
          btnNextStep.classList.contains('disabled')
        ) {
          return;
        }
        const nextStep = this.getNextStepKey();
        if (nextStep) {
          const nextNumber = this.getStepNumber(nextStep);
          this.orchestrator.selectionState.reached = Math.max(
            this.orchestrator.selectionState.reached,
            nextNumber,
          );
          this.setActiveStep(nextStep);
        }
      });
    }

    if (btnBackStep) {
      btnBackStep.addEventListener('click', () => {
        const previous = this.getPreviousStepKey();
        if (previous) this.setActiveStep(previous);
      });
    }

    this.setupArtsScrollbar();
    this.syncStepNavAvailability();
    this.syncNextStepButton();
    this.syncFooterNotes();
  }
}

window.StepNavigationController = StepNavigationController;
